/**
 * `/api/family-cart` fetchers + wire converters — the REST surface behind
 * the server-synced parent cart (the "Cart + week-lunchbox queries land
 * when …" promise in `orders-family.ts`, now shipped for the cart).
 *
 * Pure data layer: fetch functions and wire↔store converters only. The
 * sync runtime (hydration query, pending-edit bookkeeping, debounced
 * flush) lives with the store in `@/stores/family-cart` so this module
 * stays import-cycle-free.
 */

import { cartLineKey, type FamilyCartLine } from '@/lib/family-orders/cart'

import { apiFetch } from './http'
import type { Mealtime } from './school-orders'

export const familyCartQueryKey = ['family-cart'] as const

/** One draft-cart line on the wire — `FamilyCartLine` minus its derived `key`. */
export interface FamilyCartLineWire {
  locationId: string
  organizationId: string
  /** `YYYY-MM-DD` */
  date: string
  mealtime: Mealtime
  studentMemberId: string
  studentUserId: string
  gradeRangeId: string | null
  menuItemId: string
  name: string
  imageUri: string | null
  unitPriceCents: number | null
  currency: string | null
  quantity: number
  specialDietAccommodationId: string | null
}

export interface FamilyCartResponse {
  lines: FamilyCartLineWire[]
}

export interface FamilyCartRejectedLine {
  index: number
  error: string
  message: string
}

export interface FamilyCartPushResponse extends FamilyCartResponse {
  rejected: FamilyCartRejectedLine[]
}

export async function fetchFamilyCart(signal?: AbortSignal): Promise<FamilyCartResponse> {
  return apiFetch<FamilyCartResponse>('/api/family-cart', { signal })
}

/**
 * Batch of ABSOLUTE upserts — "this line's quantity is now N" (N <= 0
 * removes it). Idempotent, so the flush engine can safely retry.
 */
export async function pushFamilyCartLines(lines: FamilyCartLineWire[]): Promise<FamilyCartPushResponse> {
  return apiFetch<FamilyCartPushResponse>('/api/family-cart/lines', { method: 'POST', body: { lines } })
}

export async function clearFamilyCartLocation(locationId: string): Promise<{ ok: true }> {
  return apiFetch<{ ok: true }>('/api/family-cart/clear', { method: 'POST', body: { locationId } })
}

export function wireToCartLine(wire: FamilyCartLineWire): FamilyCartLine {
  return {
    key: cartLineKey(wire.date, wire.mealtime, wire.studentMemberId, wire.menuItemId, wire.specialDietAccommodationId),
    locationId: wire.locationId,
    organizationId: wire.organizationId,
    date: wire.date,
    mealtime: wire.mealtime,
    studentMemberId: wire.studentMemberId,
    studentUserId: wire.studentUserId,
    gradeRangeId: wire.gradeRangeId,
    menuItemId: wire.menuItemId,
    name: wire.name,
    imageUri: wire.imageUri,
    unitPriceCents: wire.unitPriceCents,
    currency: wire.currency,
    quantity: wire.quantity,
    specialDietAccommodationId: wire.specialDietAccommodationId,
  }
}

export function cartLineToWire(line: FamilyCartLine): FamilyCartLineWire {
  return {
    locationId: line.locationId,
    organizationId: line.organizationId,
    date: line.date,
    mealtime: line.mealtime,
    studentMemberId: line.studentMemberId,
    studentUserId: line.studentUserId,
    gradeRangeId: line.gradeRangeId,
    menuItemId: line.menuItemId,
    name: line.name,
    imageUri: line.imageUri,
    unitPriceCents: line.unitPriceCents,
    currency: line.currency,
    quantity: line.quantity,
    specialDietAccommodationId: line.specialDietAccommodationId ?? null,
  }
}
